import Optimization from '../optimization/optimization';
import Implementation from '../implementation/implementation';
import CircuitResults from '../results/circuit-results';
import { applyAdditionalCriteria } from '../../services/opamps/ratings';
import { getStageInputsInImplementationOrder } from '../utils/stage';
import { getTopOpAmp } from '@ebiz/aditools-filter/recommendations';
import FilterConstants from 'filter-wizard/filter-constants';

export function getOpampNames(opAmpsList, indexModel) {
  const individualStageInputs = getStageInputsInImplementationOrder(
    indexModel.inputs.components.individualStageInputs,
    indexModel.results.components.detailsOfCircuits,
  );

  const noiseResistanceParams = _getNoiseResistanceParams(indexModel);
  const noiseResistance = _getNoiseResistance(
    noiseResistanceParams.circuits,
    noiseResistanceParams.adcBandwidth,
    noiseResistanceParams.frequencyArray,
  );

  const modelParams = {
    opAmpsList: opAmpsList,
    circuits: indexModel.results.components.circuits,
    individualStageInputs: individualStageInputs,
    vsPlus: indexModel.inputs.components.vsPlus,
    vsMinus: indexModel.inputs.components.vsMinus,
    optimization: indexModel.inputs.components.optimization,
    stopbandFrequency: indexModel.results.components.stopbandFrequency,
    totalGain: indexModel.results.specifications.totalGain,
  };
  const params = indexModel.opAmpSource.getFilterOpampsParams(modelParams);
  const topOpAmp = getTopOpAmp(
    params.opAmps,
    params.circuits,
    params.vsPlus,
    params.vsMinus,
    params.optimization,
    noiseResistance,
    indexModel.results.specifications.totalGain,
    params.stopbandFrequency,
    applyAdditionalCriteria,
  );

  const targetStages = indexModel.results.specifications.targetStagesWithDistributedGain;
  const opampNames = targetStages.map(() => {
    if (topOpAmp && topOpAmp.partNumber) {
      return topOpAmp.partNumber;
    }
  });

  return opampNames;
}

export function getCircuitValues(circuits, individualStageInputs) {
  if (Array.isArray(circuits)) {
    return circuits.map((circuit, index) => {
      const type = circuit.algorithm === 'lowpassV2' ? 'lowpass' : circuit.algorithm;
      return {
        c1: circuit.c1 ? (typeof circuit.c1.value !== 'undefined' ? circuit.c1.value : circuit.c1) : null,
        c2: circuit.c2 ? (typeof circuit.c2.value !== 'undefined' ? circuit.c2.value : circuit.c2) : null,
        c5: circuit.c5 ? (typeof circuit.c5.value !== 'undefined' ? circuit.c5.value : circuit.c5) : null,
        fp: circuit.stageFp,
        gain: circuit.gain,
        implementation: getImplementation(individualStageInputs[index].implementation),
        q: circuit.stageQ,
        r1: circuit.r1 ? (typeof circuit.r1.value !== 'undefined' ? circuit.r1.value : circuit.r1) : null,
        r2: circuit.r2 ? (typeof circuit.r2.value !== 'undefined' ? circuit.r2.value : circuit.r2) : null,
        r3: circuit.r3 ? (typeof circuit.r3.value !== 'undefined' ? circuit.r3.value : circuit.r3) : null,
        r4: circuit.r4 ? (typeof circuit.r4.value !== 'undefined' ? circuit.r4.value : circuit.r4) : null,
        r5: circuit.r5 ? (typeof circuit.r5.value !== 'undefined' ? circuit.r5.value : circuit.r5) : null,
        type: type,
      };
    });
  } else {
    const type = circuits.algorithm === 'lowpassV2' ? 'lowpass' : circuits.algorithm;
    return [
      {
        c1: circuits.c1 ? (typeof circuits.c1.value !== 'undefined' ? circuits.c1.value : circuits.c1) : null,
        c2: circuits.c2 ? (typeof circuits.c2.value !== 'undefined' ? circuits.c2.value : circuits.c2) : null,
        c5: circuits.c5 ? (typeof circuits.c5.value !== 'undefined' ? circuits.c5.value : circuits.c5) : null,
        fp: circuits.stageFp,
        gain: circuits.gain,
        implementation: getImplementation(individualStageInputs.implementation),
        q: circuits.stageQ,
        r1: circuits.r1 ? (typeof circuits.r1.value !== 'undefined' ? circuits.r1.value : circuits.r1) : null,
        r2: circuits.r2 ? (typeof circuits.r2.value !== 'undefined' ? circuits.r2.value : circuits.r2) : null,
        r3: circuits.r3 ? (typeof circuits.r3.value !== 'undefined' ? circuits.r3.value : circuits.r3) : null,
        r4: circuits.r4 ? (typeof circuits.r4.value !== 'undefined' ? circuits.r4.value : circuits.r4) : null,
        r5: circuits.r5 ? (typeof circuits.r5.value !== 'undefined' ? circuits.r5.value : circuits.r5) : null,
        type: type,
      },
    ];
  }
}

export function getImplementation(implementation) {
  if (implementation === FilterConstants.implementationType.firstOrder) {
    return 'activeFirstOrder';
  }
  if (implementation === FilterConstants.implementationType.inductor) {
    return 'activeInductor';
  }
  if (implementation === FilterConstants.implementationType.deliyannisFriend) {
    return 'multipleFeedback';
  }
  return implementation;
}

function _getNoiseResistance(circuits, adcBandwidth, frequencyArray) {
  var highVNoiseOpAmp = { gbw: 1e12, rOut: 1e-12, vNoise: 1e-6, iNoise: 0 };
  var circuitResultsCopy = [];
  for (let i = 0; i < circuits.length; i++) {
    let circuitCopy = circuits[i];
    circuitCopy.opAmp = highVNoiseOpAmp;
    if (i > 0) {
      circuitCopy.previousCircuit = circuitResultsCopy[i - 1];
    }

    let circuitResult = CircuitResults.create({
      circuit: circuitCopy,
      adcBandwidth: adcBandwidth,
      frequencyArray: frequencyArray,
    });
    circuitResultsCopy.push(circuitResult);
  }

  var lastIndex = circuitResultsCopy.length - 1;
  var spectralDensityHighVNoise = circuitResultsCopy[lastIndex].noiseGroup.totalSpectralNoiseReferredToOutputArray;

  var highINoiseOpAmp = { gbw: 1e12, rOut: 1e-12, vNoise: 0, iNoise: 1e-6 };
  circuitResultsCopy = [];
  for (let i = 0; i < circuits.length; i++) {
    let circuitCopy = circuits[i];
    circuitCopy.opAmp = highINoiseOpAmp;
    if (i > 0) {
      circuitCopy.previousCircuit = circuitResultsCopy[i - 1];
    }

    let circuitResult = CircuitResults.create({
      circuit: circuitCopy,
      adcBandwidth: adcBandwidth,
      frequencyArray: frequencyArray,
    });
    circuitResultsCopy.push(circuitResult);
  }

  var spectralDensityHighINoise = circuitResultsCopy[lastIndex].noiseGroup.totalSpectralNoiseReferredToOutputArray;

  var length = spectralDensityHighVNoise.length;
  var averageResistance = null;
  for (let j = 0; j < length; j++) {
    var noiseResistance = spectralDensityHighINoise[j] / spectralDensityHighVNoise[j];
    averageResistance += noiseResistance / length;
  }

  return averageResistance;
}

function _getNoiseResistanceParams(indexModel) {
  const optimizationType = indexModel.inputs.components.optimization;
  const noiseInput = indexModel.inputs.noise;
  const specificationResults = indexModel.results.specifications;
  const targetStages = specificationResults.targetStagesWithDistributedGain;

  const adcBandwidth =
    noiseInput && noiseInput.adcBandwidth
      ? noiseInput.adcBandwidth
      : specificationResults.filter.getAdcFrequency(specificationResults.filterFrequencyRange.stopFrequency);

  const frequencyArray = targetStages.map((stage) => {
    return stage.stageFp;
  });

  const optimization = Optimization.create({ optimization: optimizationType });
  const circuits = _getCircuits(targetStages, optimization);

  return {
    adcBandwidth: adcBandwidth,
    frequencyArray: frequencyArray,
    circuits: circuits,
  };
}

function _getCircuits(targetStages, optimization) {
  var circuitsArray = [];

  for (let i = 0; i < targetStages.length; i++) {
    var stage = targetStages[i];
    var defaultImplementation = stage.defaultImplementation;
    var implementation = Implementation.create({
      selectedImplementation: defaultImplementation,
      filter: stage.filter,
    });

    var stageFp = stage.stageFp;
    var stageQ = stage.stageQ;
    var gain = stage.gain;

    let circuit = implementation.createCircuit(stageFp, gain, stageQ, optimization);

    circuitsArray.push(circuit);
  }

  return circuitsArray;
}
